import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  FileText,
  Mail,
  Phone,
  Send,
  CheckCircle,
  AlertCircle,
  Clock,
  ExternalLink,
  ArrowRight,
  MessageCircle,
  Check
} from 'lucide-react';
import Modal from './Modal';
import { Driver } from '../types';
import { DriverInvoice, DriverInvoiceService } from '../services/driverInvoiceService';
import { supabase } from '../services/supabaseClient';
import { useToast } from '../context/ToastContext';
import { MessageTemplates, type InvoiceTicketSummary } from '../services/messageTemplates';

interface InvoiceLineItem {
  id: string;
  item_type: string;
  description: string;
  ticket_id?: string;
  hire_id?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  metadata?: Record<string, any>;
}

interface InvoiceTicketDetails {
  id: string;
  pcn_number?: string | null;
  vrn?: string | null;
  issuing_authority?: string | null;
}

interface InvoiceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: DriverInvoice;
  driver?: Driver;
  onUpdate: () => void;
}

const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  isOpen,
  onClose,
  invoice,
  driver,
  onUpdate
}) => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  // State
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [copiedWhatsApp, setCopiedWhatsApp] = useState(false);
  const [organisationData, setOrganisationData] = useState<{ name: string; phone?: string } | null>(null);
  const [ticketDetailsById, setTicketDetailsById] = useState<Record<string, InvoiceTicketDetails>>({});

  useEffect(() => {
    if (isOpen && invoice) {
      loadLineItems();
      loadOrganisationData();
    }
  }, [isOpen, invoice]);

  const loadLineItems = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('driver_invoice_items')
        .select('*')
        .eq('invoice_id', invoice.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      const loadedItems = data || [];
      setLineItems(loadedItems);

      const ticketIds = Array.from(
        new Set(
          loadedItems
            .map((item) => item.ticket_id)
            .filter((ticketId): ticketId is string => Boolean(ticketId))
        )
      );

      if (ticketIds.length === 0) {
        setTicketDetailsById({});
      } else {
        const { data: ticketData, error: ticketError } = await supabase
          .from('tickets')
          .select('id, pcn_number, vrn, issuing_authority')
          .in('id', ticketIds);

        if (ticketError) {
          console.error('Error loading ticket details for WhatsApp template:', ticketError);
          setTicketDetailsById({});
        } else {
          const mapped: Record<string, InvoiceTicketDetails> = {};
          for (const ticket of (ticketData || []) as InvoiceTicketDetails[]) {
            mapped[ticket.id] = ticket;
          }
          setTicketDetailsById(mapped);
        }
      }
    } catch (error) {
      console.error('Error loading line items:', error);
      addToast('Failed to load invoice details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganisationData = async () => {
    try {
      const { data, error } = await supabase
        .from('organisations')
        .select('name')
        .eq('id', invoice.organisation_id)
        .single();

      if (error) throw error;
      setOrganisationData(data);
    } catch (error) {
      console.error('Error loading organisation data:', error);
      setOrganisationData({ name: 'PCN Guard' });
    }
  };

  const handleSendInvoice = async () => {
    setSending(true);
    try {
      const result = await DriverInvoiceService.sendInvoice(invoice.id);
      if (result.success) {
        addToast('Invoice sent successfully', 'success');
        onUpdate();
      } else {
        addToast(result.error || 'Failed to send invoice', 'error');
      }
    } catch (error: any) {
      addToast(error.message || 'Failed to send invoice', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleMarkPaid = async () => {
    setMarkingPaid(true);
    try {
      const result = await DriverInvoiceService.markInvoicePaid(invoice.id, 'manual');
      if (result.success) {
        addToast('Invoice marked as paid', 'success');
        onUpdate();
        onClose();
      } else {
        addToast(result.error || 'Failed to mark invoice as paid', 'error');
      }
    } catch (error: any) {
      addToast(error.message || 'Failed to mark invoice as paid', 'error');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleCopyWhatsAppMessage = async () => {
    if (!driver) {
      addToast('Missing driver information', 'error');
      return;
    }

    try {
      let message: string;
      const organisationName = organisationData?.name || 'PCN Guard';
      const organisationPhone = organisationData?.phone;
      const daysOverdue = MessageTemplates.calculateDaysOverdue(invoice.due_date);
      let stripeInvoiceUrl = String(invoice.stripe_invoice_url || '').trim();
      if (!stripeInvoiceUrl) {
        const { data: latestInvoice } = await supabase
          .from('driver_invoices')
          .select('stripe_invoice_url')
          .eq('id', invoice.id)
          .single();
        stripeInvoiceUrl = String(latestInvoice?.stripe_invoice_url || '').trim();
      }
      const paymentUrl = stripeInvoiceUrl || `${window.location.origin}/driver-invoice/${invoice.id}`;
      const paymentLinkLabel = stripeInvoiceUrl ? 'Pay now (Stripe)' : 'Pay online';

      const ticketDetails: InvoiceTicketSummary[] = lineItems
        .filter((item) => item.item_type === 'ticket_charge')
        .map((item) => {
          const ticket = item.ticket_id ? ticketDetailsById[item.ticket_id] : undefined;
          const metadata = (item.metadata || {}) as Record<string, any>;
          return {
            pcnNumber: String(ticket?.pcn_number || metadata.pcn_number || item.description || 'Unknown PCN'),
            vrn: String(ticket?.vrn || metadata.vrn || 'Unknown VRN'),
            council: String(ticket?.issuing_authority || metadata.issuing_authority || 'Unknown Council'),
            amount: Number(item.total_price || 0)
          };
        });

      if (invoice.status === 'paid') {
        // Payment confirmation message
        message = MessageTemplates.generatePaymentConfirmationMessage({
          driverName: driver.name,
          invoiceNumber: invoice.invoice_number,
          totalAmount: Number(invoice.total_amount),
          receiptUrl: invoice.pdf_url,
          organisationName,
          ticketDetails
        });
      } else if (['sent', 'viewed', 'overdue'].includes(invoice.status)) {
        // Payment reminder message
        message = MessageTemplates.generatePaymentReminderMessage({
          driverName: driver.name,
          invoiceNumber: invoice.invoice_number,
          totalAmount: Number(invoice.total_amount),
          dueDate: MessageTemplates.formatDate(invoice.due_date),
          daysOverdue: daysOverdue,
          lateFee: Number(invoice.late_fees) || 0,
          invoiceUrl: paymentUrl,
          paymentLinkLabel,
          organisationName,
          ticketDetails
        });
      } else {
        // Invoice notification message (draft or new)
        message = MessageTemplates.generateInvoiceMessage({
          driverName: driver.name,
          invoiceNumber: invoice.invoice_number,
          totalAmount: Number(invoice.total_amount),
          dueDate: MessageTemplates.formatDate(invoice.due_date),
          invoiceUrl: paymentUrl,
          paymentLinkLabel,
          organisationName,
          organisationPhone,
          ticketDetails
        });
      }

      const success = await MessageTemplates.copyToClipboard(message);
      if (success) {
        addToast('WhatsApp message copied to clipboard', 'success');
        setCopiedWhatsApp(true);
        setTimeout(() => setCopiedWhatsApp(false), 3000);
      } else {
        addToast('Failed to copy message to clipboard', 'error');
      }
    } catch (error: any) {
      console.error('Error copying WhatsApp message:', error);
      addToast(error.message || 'Failed to copy WhatsApp message', 'error');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
      case 'sent':
      case 'viewed':
        return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
      case 'overdue':
        return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
      case 'disputed':
        return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
      case 'draft':
        return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle size={16} />;
      case 'sent':
      case 'viewed':
        return <Send size={16} />;
      case 'overdue':
        return <AlertCircle size={16} />;
      case 'disputed':
        return <AlertCircle size={16} />;
      case 'draft':
        return <FileText size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const getItemTypeLabel = (type: string) => {
    switch (type) {
      case 'ticket_charge':
        return 'Ticket Charge';
      case 'admin_fee':
        return 'Admin Fee';
      case 'late_fee':
        return 'Late Fee';
      case 'discount':
        return 'Discount';
      default:
        return type;
    }
  };

  const platformFeePercentage = Number(invoice.platform_fee_percentage || 0);
  const platformFeeAmount = Number(
    invoice.platform_fee_amount ??
    (platformFeePercentage > 0 ? Number(invoice.total_amount || 0) * (platformFeePercentage / 100) : 0)
  );
  const estimatedNetPayout = Math.max(0, Number(invoice.total_amount || 0) - platformFeeAmount);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Invoice ${invoice.invoice_number}`}
      maxWidth="max-w-3xl"
    >
      <div className="p-6">
        {/* Header Info */}
        <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(invoice.status)}`}>
                {getStatusIcon(invoice.status)}
                {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
              </span>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Created on {new Date(invoice.created_at).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {invoice.status === 'draft' && (
              <button
                onClick={handleSendInvoice}
                disabled={sending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 flex items-center gap-2 disabled:opacity-50"
              >
                {sending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                Send Invoice
              </button>
            )}
            {['sent', 'viewed', 'overdue'].includes(invoice.status) && (
              <button
                onClick={handleMarkPaid}
                disabled={markingPaid}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
              >
                {markingPaid ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CheckCircle size={16} />
                )}
                Mark Paid
              </button>
            )}
            {/* WhatsApp Copy Button - Available for all statuses except cancelled */}
            {invoice.status !== 'cancelled' && driver && (
              <button
                onClick={handleCopyWhatsAppMessage}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                title="Copy pre-formatted WhatsApp message to clipboard"
              >
                {copiedWhatsApp ? (
                  <>
                    <Check size={16} />
                    Copied!
                  </>
                ) : (
                  <>
                    <MessageCircle size={16} />
                    Copy WhatsApp
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Driver Info */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
              Bill To
            </h4>
            {driver ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-lg font-bold text-slate-600 dark:text-slate-300">
                    {driver.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 dark:text-white">{driver.name}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Driver</p>
                  </div>
                </div>
                {driver.email && (
                  <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <Mail size={14} />
                    {driver.email}
                  </div>
                )}
                {driver.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <Phone size={14} />
                    {driver.phone}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400">Driver information not available</p>
            )}
          </div>

          {/* Invoice Details */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
              Invoice Details
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Invoice Number</span>
                <span className="font-mono font-semibold text-slate-800 dark:text-white">{invoice.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Invoice Date</span>
                <span className="text-slate-800 dark:text-white">
                  {new Date(invoice.invoice_date).toLocaleDateString('en-GB')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Due Date</span>
                <span className={`font-medium ${
                  invoice.status === 'overdue' ? 'text-red-600 dark:text-red-400' : 'text-slate-800 dark:text-white'
                }`}>
                  {new Date(invoice.due_date).toLocaleDateString('en-GB')}
                </span>
              </div>
              {invoice.billing_type === 'monthly' && invoice.period_start && invoice.period_end && (
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Period</span>
                  <span className="text-slate-800 dark:text-white">
                    {new Date(invoice.period_start).toLocaleDateString('en-GB')} - {new Date(invoice.period_end).toLocaleDateString('en-GB')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-slate-800 dark:text-white mb-3">
            Line Items
          </h4>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-primary-600" />
            </div>
          ) : lineItems.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
              <FileText size={32} className="mx-auto text-slate-400 dark:text-slate-500 mb-2" />
              <p className="text-sm text-slate-500 dark:text-slate-400">No line items</p>
            </div>
          ) : (
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Description</th>
                    <th className="text-left px-4 py-3 font-semibold">Type</th>
                    <th className="text-right px-4 py-3 font-semibold">Qty</th>
                    <th className="text-right px-4 py-3 font-semibold">Unit Price</th>
                    <th className="text-right px-4 py-3 font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map(item => (
                    <tr key={item.id} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-4 py-3">
                        <p className="text-slate-800 dark:text-white">{item.description}</p>
                        {item.metadata?.pcn_number && item.ticket_id && (
                          <button
                            onClick={() => {
                              onClose();
                              navigate(`/tickets/${item.ticket_id}`);
                            }}
                            className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1 mt-0.5"
                          >
                            PCN: {item.metadata.pcn_number}
                            <ArrowRight size={10} />
                          </button>
                        )}
                        {item.metadata?.pcn_number && !item.ticket_id && (
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            PCN: {item.metadata.pcn_number}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                          item.item_type === 'ticket_charge'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : item.item_type === 'admin_fee'
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                            : item.item_type === 'discount'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'
                        }`}>
                          {getItemTypeLabel(item.item_type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400">
                        {item.quantity}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400">
                        £{Number(item.unit_price).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-800 dark:text-white">
                        {item.item_type === 'discount' ? '-' : ''}£{Number(item.total_price).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4">
          <div className="space-y-2 text-sm max-w-xs ml-auto">
            <div className="flex justify-between text-slate-600 dark:text-slate-400">
              <span>Ticket Charges</span>
              <span>£{Number(invoice.ticket_charges_total || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-slate-600 dark:text-slate-400">
              <span>Admin Fees</span>
              <span>£{Number(invoice.admin_fees_total || 0).toFixed(2)}</span>
            </div>
            {Number(invoice.late_fees) > 0 && (
              <div className="flex justify-between text-slate-600 dark:text-slate-400">
                <span>Late Fees</span>
                <span>£{Number(invoice.late_fees).toFixed(2)}</span>
              </div>
            )}
            {Number(invoice.discount_amount) > 0 && (
              <div className="flex justify-between text-green-600 dark:text-green-400">
                <span>Discount</span>
                <span>-£{Number(invoice.discount_amount).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-lg text-slate-800 dark:text-white pt-2 border-t border-slate-200 dark:border-slate-700">
              <span>Total</span>
              <span>£{Number(invoice.total_amount).toFixed(2)}</span>
            </div>
            {platformFeeAmount > 0 && (
              <>
                <div className="flex justify-between text-amber-700 dark:text-amber-400">
                  <span>Platform Fee ({platformFeePercentage.toFixed(2)}%)</span>
                  <span>£{platformFeeAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-700 dark:text-slate-300 font-medium">
                  <span>Estimated Net Payout</span>
                  <span>£{estimatedNetPayout.toFixed(2)}</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 text-right">
                  Platform fee shown for billing transparency.
                </p>
              </>
            )}
            {invoice.status !== 'paid' && Number(invoice.amount_paid) > 0 && (
              <>
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Paid</span>
                  <span>-£{Number(invoice.amount_paid).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-primary-600 dark:text-primary-400">
                  <span>Amount Due</span>
                  <span>£{Number(invoice.amount_due).toFixed(2)}</span>
                </div>
              </>
            )}
            {invoice.status === 'paid' && (
              <div className="flex items-center justify-end gap-2 text-green-600 dark:text-green-400 pt-2">
                <CheckCircle size={16} />
                <span className="font-medium">Paid in Full</span>
              </div>
            )}
          </div>
        </div>

        {/* Timeline / Activity */}
        {(invoice.sent_at || invoice.viewed_at || invoice.paid_at) && (
          <div className="mt-6">
            <h4 className="text-sm font-semibold text-slate-800 dark:text-white mb-3">
              Activity
            </h4>
            <div className="space-y-3">
              {invoice.paid_at && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <CheckCircle size={16} className="text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 dark:text-white">Payment received</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(invoice.paid_at).toLocaleString('en-GB')}
                    </p>
                  </div>
                </div>
              )}
              {invoice.viewed_at && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <ExternalLink size={16} className="text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 dark:text-white">Invoice viewed</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(invoice.viewed_at).toLocaleString('en-GB')}
                    </p>
                  </div>
                </div>
              )}
              {invoice.sent_at && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    <Send size={16} className="text-slate-600 dark:text-slate-400" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 dark:text-white">Invoice sent</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(invoice.sent_at).toLocaleString('en-GB')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end mt-6 pt-4 border-t border-slate-100 dark:border-slate-800">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default InvoiceDetailModal;
